import { API_BASE_URL } from "src/config/api";
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
    Card,
    CardHeader,
    Typography,
    Box,
    Stack,
    Divider,
    CircularProgress,
} from '@mui/material';
import { format } from 'date-fns';

TransactionCard.propTypes = {
    title: PropTypes.string,
    subheader: PropTypes.string,
    userId: PropTypes.string.isRequired,
    token: PropTypes.string.isRequired,
};

export default function TransactionCard(props) {
    const { title, subheader } = props;
    const userId = props.admin ? props.userId : localStorage.getItem('userId');
    const token = localStorage.getItem('token');
    const [transactions, setTransactions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchTransactions = async () => {
            setIsLoading(true);
            try {
                const cleanToken = token.replace(/"/g, '');
                const config = {
                    headers: {
                        Authorization: `Bearer ${cleanToken}`,
                    },
                };
                const response = await axios.get(
                    `${API_BASE_URL}/getHistoryOfWallet?userId=${userId}`,
                    config
                );

                const rawData = response.data?.data || [];

                const sortedTxns = rawData
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                    .slice(0, 5)
                    .map((txn) => ({
                        createdAt: txn.createdAt,
                        balance: txn.balance,
                        type: txn.transactionType === 'credit' ? 'Credit' : 'Debit',
                        reason: txn.transactionReason,
                    }));

                setTransactions(sortedTxns);
            } catch (error) {
                console.error('Error fetching wallet history data:', error);
                setTransactions([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTransactions();
    }, [userId, token]);

    return (
        <Card sx={{ height: '484px' }}>
            <CardHeader title={title} subheader={subheader} />
            <Box sx={{ px: 3, py: 2, overflowY: 'auto', height: '100%' }}>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" mt={5}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <Stack spacing={2}>
                        {transactions.length === 0 ? (
                            <Box display="flex" justifyContent="center" alignItems="center" height="350px">
                                <Typography variant="body2" color="text.secondary" textAlign="center">
                                    No transaction data available.
                                </Typography>
                            </Box>
                        ) : (
                            transactions.map((txn, idx) => (
                                <Box key={idx}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight="bold">
                                                {txn.reason}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {format(new Date(txn.createdAt), 'dd MMM yyyy, hh:mm a')}
                                            </Typography>
                                        </Box>

                                        <Box textAlign="right">
                                            <Typography
                                                variant="subtitle2"
                                                color={txn.type === 'Credit' ? 'success.dark' : 'error.main'}
                                                fontWeight="bold"
                                            >
                                                ${txn.balance}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {txn.type}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    {idx !== transactions.length - 1 && <Divider sx={{ mt: 1.5 }} />}
                                </Box>
                            ))
                        )}
                    </Stack>
                )}
            </Box>
        </Card>
    );
}
